import type { FC } from "react"
import React, { useEffect, useState } from "react"

import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react"
import { useTranslation } from "react-i18next"
import { atom, useRecoilState, useRecoilValue } from "recoil"
import styled from "styled-components"

import { buttonStyle } from "~/components/Button"
import { currentUserIsDesignRequestUserState } from "~/containers/Proposal/state/user/user-state"
import { useTriggerPreventionToast } from "~/hooks/useTriggerPreventionToast"
import { fontWeight } from "~/utils/styles"

import panelPlacementEraser from "../../../assets/svg/phase/autoPlacementEraser.svg"
import panelPlacementIcon from "../../../assets/svg/phase/autoPlacementPanelIcon.svg"
import panelPlacementWand from "../../../assets/svg/phase/autoPlacementWand.svg"
import { panelIndex } from "../state/elements/panels"
import { useAutoPlacePanels } from "../state/elements/transactions/autoPlacePanels"
import { useDeletePanels } from "../state/elements/transactions/deletePanels"

const Card = styled.div<{ panelsAutoPlaced: boolean }>`
  background-color: #e8f0fc;
  height: 100;
  display: grid;
  border-radius: 4px;
  padding-bottom: 4px;
  div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 8px;
    padding: 8px 4px;
  }
  div p {
    font-size: 16px;
    ${fontWeight(600)};
    width: ${({ panelsAutoPlaced }) => (panelsAutoPlaced ? `70%` : `50%`)};
  }
  button {
    ${buttonStyle}
    min-width: 150;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 36px;
    background-color: ${({ panelsAutoPlaced }) =>
      panelsAutoPlaced ? `var(--white)` : `var(--blue)`};
    color: ${({ panelsAutoPlaced }) =>
      panelsAutoPlaced ? `#295CA0` : `#FFFFFF`};
    margin: 8px;
    position: relative;
  }
`
const EraseTooltip = styled.div`
  z-index: 100;
  background: var(--blue);
  height: 88px;
  color: var(--white);
  left: 254px;
  border-radius: 6px;
  padding: 10px;
  width: 250px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  h3 {
    font-size: 16px;
    line-height: 17px;
    ${fontWeight(600)};
    margin-bottom: 8px;
  }
  p {
    font-size: 15px;
    line-height: 16px;
    ${fontWeight(400)};
  }
  div {
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    position: absolute;
    left: -10px;
    top: 38%;
    border-right: var(--blue) 10px solid;
  }
`

export const showAutoPlacePanelState = atom<boolean>({
  key: `panelsAutoPlaced`,
  default: false,
})

const AutoPlacementPanelButton_Internal: FC = () => {
  const [openEraseTooltip, setOpenEraseTooltip] = useState(false)
  const { t } = useTranslation()
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )
  const [panelsAutoPlaced, setPanelsAutoPlaced] = useRecoilState(
    showAutoPlacePanelState,
  )
  const panelIds = useRecoilValue(panelIndex)
  const autoPlacePanels = useAutoPlacePanels()
  const deletePanels = useDeletePanels()
  const canPlace = currentUserIsDesignUser ? true : false
  const triggerPreventionToast = useTriggerPreventionToast()

  const { y, refs, strategy, context } = useFloating({
    open: openEraseTooltip,
    onOpenChange: setOpenEraseTooltip,
    placement: `right`,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: `start`,
      }),
      shift(),
    ],
  })
  const hover = useHover(context, { move: false })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  useEffect(() => {
    if (panelIds.length === 0) {
      setPanelsAutoPlaced(false)
    } else {
      setPanelsAutoPlaced(true)
    }
  }, [panelIds, setPanelsAutoPlaced, panelsAutoPlaced])

  const handleAutoPlaceClick = () => {
    if (currentUserIsDesignUser) {
      if (panelsAutoPlaced) {
        deletePanels(panelIds)
      } else {
        autoPlacePanels()
      }
    } else {
      triggerPreventionToast()
    }
  }

  return (
    <Card panelsAutoPlaced={panelsAutoPlaced}>
      <div>
        <p>{panelsAutoPlaced ? t(`panelsAutoPlaced`) : t(`autoPlacePanels`)}</p>
        <img
          src={panelPlacementIcon}
          alt="panelPlacementWand"
          style={{ width: 46.5, height: 45.6 }}
        />
      </div>
      <button
        className={!currentUserIsDesignUser ? `blocked` : ``}
        disabled={currentUserIsDesignUser ? !canPlace : false}
        onClick={handleAutoPlaceClick}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <FloatingPortal>
          {openEraseTooltip && panelsAutoPlaced && (
            <EraseTooltip
              ref={refs.setFloating}
              style={{
                position: strategy,
                top: y ?? 0,
              }}
              {...getFloatingProps()}
            >
              <h3>Erase Layout</h3>
              <p>
                If you click on Erase Layout, the Auto-Layout will be canceled
                and the Layout will be cleared.
              </p>
              <div />
            </EraseTooltip>
          )}
        </FloatingPortal>
        <img
          src={panelsAutoPlaced ? panelPlacementEraser : panelPlacementWand}
          alt="panelPlacementWand"
          style={{ width: 16, marginRight: 8 }}
        />
        {panelsAutoPlaced ? t(`eraseLayout`) : t(`autoPlacePanels`)}
      </button>
    </Card>
  )
}
export const AutoPlacementPanelButton = React.memo(
  AutoPlacementPanelButton_Internal,
)
